import React, { useCallback, useMemo, useState } from "react";
import "./../../styles/homePage/patientsList.scss";
import TablePage from "../../components/patientsList/genericTable/TablePage";
import { useTranslation } from "react-i18next";
import { TableCard } from "../../common/types/tableCardType";
import { useQuery } from "@tanstack/react-query";
import {
  exportUsers,
  getAllUsers,
  getUserCardsData,
} from "../../common/api/apiCalls";
import { svgs } from "../../common/assats";
import { useNavigate } from "react-router-dom";
import { debounce } from "@mui/material";
import { GenericRecord } from "../../common/types/filterObjType";
import { getAllSites } from "../../common/api/siteService";
import { organizeUserData } from "../../common/function/userData";
import "./Users.scss";
import GenericList from "../../components/GenericList/GenericList";
import Modal from "../../components/Modal/Modal";
import Loader from "../../components/Loader/Loader";

const sortKeyArray = [
  "firstName",
  "lastName",
  "identityNumber",
  "role",
  "email",
  "center",
  "status",
  "relatedPatients",
];

const Users = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>(""); // Debounced value for API
  const [sortKey, setSortKey] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");
  const [filters, setFilters] = useState<string[]>([]);

  // Modal state for showing related patients
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPatients, setSelectedPatients] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<{
    role: string;
    firstName: string;
    lastName: string;
  } | null>(null);

  const getText = (text: string) => t(`usersList.${text}`);

  const {
    data: usersList,
    error: usersFetchError,
    isLoading: isLoadingUsers,
  } = useQuery({
    queryKey: ["usersData", sortKey, sortOrder, debouncedSearch, filters],
    queryFn: () => getAllUsers(sortKey, sortOrder, debouncedSearch, filters),
  });

  // Second query, dependent on the first one
  const {
    data: filtersData,
    error: centerDataFetchError,
    isLoading: isCenterDataLoading,
  } = useQuery({
    queryKey: ["centerData"],
    queryFn: getAllSites,
  });

  const {
    data: cardsData,
    error: userCardsError,
    isLoading: isLoadingUserCards,
  } = useQuery({
    queryKey: ["cards"],
    queryFn: getUserCardsData,
  });

  // Debounce the search input
  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setDebouncedSearch(value);
    }, 300), // 300ms delay
    []
  );

  // Update the search value when typing
  const handleSearchChange = (str: string) => {
    setSearch(str); // Instant feedback for the user
    debouncedSetSearch(str); // Delayed API request
  };

  const cards: TableCard[] = useMemo(
    () => [
      {
        headline: getText("cards.Active_Users"),
        icon: svgs.cards.ActiveUsers,
        value: isLoadingUserCards ? 0 : cardsData[0] || 0,
        action: () => {
          console.log("click");
        },
      },
      {
        headline: getText("cards.Centers"),
        icon: svgs.cards.Centers,
        value: isLoadingUserCards ? 0 : cardsData[1] || 0,
        action: () => {
          console.log("click");
        },
      },
      {
        headline: getText("cards.Patients"),
        icon: svgs.cards.Patients,
        value: isLoadingUserCards ? 0 : cardsData[2] || 0,
        action: () => {
          console.log("click");
        },
      },
    ],
    [cardsData]
  );

  function getFilters(data: { id: string; SiteName: string }[]) {
    if (!data || !Array.isArray(data)) {
      console.error("Invalid data passed to getFilters:", data);
      return {};
    }
    // Filter object for the table
    let filterObj: GenericRecord<string> = {
      center: data.map((val) => {
        return { name: val.SiteName, value: val.SiteName };
      }),
      status: [
        { name: "Active", value: "1" },
        { name: "inactive", value: "0" },
      ],
    };
    return filterObj;
  }
  function handleRowClick(index: number) {
    navigate("EditUser", {
      state: { user: usersList[index] },
    });
  }

  const handleExport = async () => {
    try {
      const blob = await exportUsers({
        sortKey,
        sortOrder,
        search: debouncedSearch,
        filters,
      });
      // Generate a downloadable URL and trigger the download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "User_List.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url); // Cleanup URL
    } catch (error) {
      console.error("Error exporting devices:", error);
      alert("Failed to export devices. Please try again.");
    }
  };

  // Handle clicking the relatedPatients button to open modal
  const handlePatientClick = (user: any) => {
    setSelectedPatients(user.relatedPatients || []);
    setSelectedUser({
      role: user.role,
      firstName: user.firstName,
      lastName: user.lastName,
    });
    setIsModalOpen(true);
  };
  if (isCenterDataLoading || isLoadingUserCards) {
    return <Loader text={getText("loading")} />;
  }
  return (
    <>
      <TablePage
        filterObj={filtersData ? getFilters(filtersData) : {}}
        tableHeadline={getText(`User_Management`)}
        headerHeadline={getText("User_Management")}
        cards={cards}
        trashAction={() => {}}
        filters={filters}
        setFilters={setFilters}
        searchValue={search}
        setSearchValue={handleSearchChange}
        searchPlaceholder={getText(
          "filter.Search_Users_by_Name,_ID_number,_Email"
        )}
        buttonText={getText("Create_New")}
        buttonAction={() => {
          navigate("CreateUser");
        }}
        setSortKey={setSortKey}
        sortKey={sortKey}
        setSortOrder={setSortOrder}
        sortOrder={sortOrder}
        tableData={
          isLoadingUsers
            ? []
            : organizeUserData(usersList).map((user) => ({
                ...user,
                relatedPatients: (
                  <button
                    className="link-button"
                    onClick={() => {
                      if (
                        user.relatedPatients &&
                        user.relatedPatients.length > 0
                      ) {
                        handlePatientClick(user);
                      }
                    }}
                  >
                    {user.relatedPatients?.length || 0}
                  </button>
                ),
              }))
        }
        sortKeysArray={sortKeyArray.map((value) => {
          return { name: getText(`sort.${value}`), val: value };
        })}
        rowClick={handleRowClick}
        subButtonText={getText("export")}
        subAction={handleExport}
        EndRowIcon={svgs.menu.More}
      />
      {/* Modal for showing related patients */}
      <Modal
        isOpen={isModalOpen}
        title="Related Patients List"
        description={
          selectedUser
            ? `${selectedUser.role}: ${selectedUser.firstName} ${selectedUser.lastName}`
            : ""
        }
        content={
          <GenericList
            items={selectedPatients}
            filterKey="firstName"
            renderItem={(patient) => (
              <div className="patient-item">
                {patient.firstName} {patient.lastName}
              </div>
            )}
            placeholder="Search patients..."
          />
        }
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default Users;
